/**
 * VariablesPanel — Variables tab for the step editor.
 * Auto-detects {{variable}} references in steps, shows editable rows,
 * allows adding/removing variables, and includes a credential picker.
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box, Typography, TextField, Button, IconButton, Tooltip, Divider,
  Alert, Autocomplete, CircularProgress,
} from '@mui/material';
import { Add as AddIcon, Close as CloseIcon } from '@mui/icons-material';
import { detectVariables } from './stepTypes';
import credentialVaultService from '../../services/api/credentialVaultService';

/** Format credential for display: "clientId - username" */
const formatCredLabel = (cred) => {
  if (!cred) return '';
  const parts = (cred.name || '').split(' - ');
  const username = parts.length > 1 ? parts.slice(1).join(' - ') : cred.name || cred.id;
  const site = cred.clientId || '';
  return site ? `${site} - ${username}` : username;
};

const VariablesPanel = ({
  steps,
  variables = {},
  onVariablesChange,
  selectedCredentialId,
  onCredentialChange,
  appKey,
}) => {
  const [credentials, setCredentials] = useState([]);
  const [loadingCreds, setLoadingCreds] = useState(false);
  const [manualVars, setManualVars] = useState([]);

  // Auto-detect variables from step definitions
  const detectedVarNames = useMemo(() => detectVariables(steps), [steps]);

  // Separate credential vars from user vars
  const credentialVars = useMemo(
    () => detectedVarNames.filter((v) => v.startsWith('credential.')),
    [detectedVarNames]
  );
  const userVars = useMemo(
    () => detectedVarNames.filter((v) => !v.startsWith('credential.')),
    [detectedVarNames]
  );

  // Merge auto-detected with manually added
  const allUserVars = useMemo(() => {
    const set = new Set([...userVars, ...manualVars]);
    return Array.from(set);
  }, [userVars, manualVars]);

  // Load credentials for the app
  useEffect(() => {
    const fetchCredentials = async () => {
      if (!appKey) return;
      setLoadingCreds(true);
      try {
        const { data } = await credentialVaultService.getAll({ appKey, limit: 100 });
        setCredentials(Array.isArray(data) ? data : data.items || []);
      } catch (err) {
        console.error('Failed to load credentials:', err);
        setCredentials([]);
      } finally {
        setLoadingCreds(false);
      }
    };
    fetchCredentials();
  }, [appKey]);

  const handleVarValueChange = useCallback(
    (varName, value) => {
      onVariablesChange({ ...variables, [varName]: value });
    },
    [variables, onVariablesChange]
  );

  const handleAddVariable = useCallback(() => {
    const newName = `var_${manualVars.length + 1}`;
    setManualVars((prev) => [...prev, newName]);
    onVariablesChange({ ...variables, [newName]: '' });
  }, [manualVars, variables, onVariablesChange]);

  const handleRemoveVariable = useCallback(
    (varName) => {
      setManualVars((prev) => prev.filter((v) => v !== varName));
      const next = { ...variables };
      delete next[varName];
      onVariablesChange(next);
    },
    [variables, onVariablesChange]
  );

  const handleRenameVariable = useCallback(
    (oldName, newName) => {
      if (!newName || newName === oldName) return;
      setManualVars((prev) => prev.map((v) => (v === oldName ? newName : v)));
      const next = { ...variables };
      next[newName] = next[oldName] || '';
      delete next[oldName];
      onVariablesChange(next);
    },
    [variables, onVariablesChange]
  );

  const hasUnfilledVars = allUserVars.some((v) => !variables[v]);

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="subtitle2" fontWeight={700} gutterBottom>
        Script Variables
      </Typography>

      {hasUnfilledVars && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Fill all variables before running
        </Alert>
      )}

      {/* Credential picker */}
      {credentialVars.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>
            Credential (auto-injected)
          </Typography>
          <Autocomplete
            size="small"
            options={credentials}
            loading={loadingCreds}
            getOptionLabel={formatCredLabel}
            value={credentials.find((c) => c.id === selectedCredentialId) || null}
            onChange={(_, cred) => onCredentialChange(cred?.id || '')}
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Credential"
                placeholder="Type to search..."
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingCreds ? <CircularProgress size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            Detected credential variables: {credentialVars.map((v) => `{{${v}}}`).join(', ')}
          </Typography>
        </Box>
      )}

      <Divider sx={{ my: 2 }} />

      {/* User variables */}
      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 1, display: 'block' }}>
        Custom Variables
      </Typography>

      {allUserVars.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          No custom variables detected in steps.
        </Typography>
      )}

      <Box sx={{ mb: 2 }}>
        {allUserVars.map((varName) => {
          const isAutoDetected = userVars.includes(varName);
          return (
            <Box
              key={varName}
              sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1.5 }}
            >
              <TextField
                label="Variable name"
                size="small"
                value={varName}
                onChange={(e) => handleRenameVariable(varName, e.target.value)}
                disabled={isAutoDetected}
                sx={{ flex: 1, '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.85rem' } }}
              />
              <TextField
                label="Value"
                size="small"
                value={variables[varName] || ''}
                onChange={(e) => handleVarValueChange(varName, e.target.value)}
                placeholder={`Enter value for {{${varName}}}`}
                sx={{ flex: 1.5 }}
              />
              {!isAutoDetected && (
                <Tooltip title="Remove variable">
                  <IconButton
                    size="small"
                    sx={{ mt: 0.5, color: '#f44336' }}
                    onClick={() => handleRemoveVariable(varName)}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          );
        })}
      </Box>

      <Button
        startIcon={<AddIcon />}
        size="small"
        variant="outlined"
        sx={{ mb: 2 }}
        onClick={handleAddVariable}
      >
        Add Variable
      </Button>

      <Divider sx={{ my: 2 }} />
      <Typography variant="caption" color="text.secondary">
        Variables are injected into step values using {'{{variable_name}}'} syntax.
        Credential fields (username, password) are auto-injected from the selected credential.
      </Typography>
    </Box>
  );
};

export default VariablesPanel;
